<script>
  /**
   * Suscripciones y pagos fijos.
   *
   * ── LO QUE ESTA PANTALLA RESPONDE ───────────────────────────────────────────
   *
   * "¿Cuánto se me va al mes en cosas que se cobran solas, y al año?"
   * Y después: qué se cobra esta semana, qué me toca pagar a mí, y cuáles
   * podría cancelar.
   *
   * El número de arriba es el ANUAL a propósito, junto al mensual. $26.900 al
   * mes no asusta a nadie; $322.800 al año por una plataforma que se usa dos
   * veces al mes, sí. Es la cifra que hace cancelar.
   *
   * ── CÓMO SE ANOTAN LOS COBROS ───────────────────────────────────────────────
   *
   * Lo que se cobra solo (tarjeta o débito automático) lo anota la app el día
   * del cobro, sin que nadie haga nada: esa plata sale igual. Lo que se paga a
   * mano no: la app recuerda y uno confirma. Toda la lógica está en
   * lib/recurrentes.js; aquí solo se pinta y se guarda.
   */
  import {
    db, METODO_PAGO, CUENTAS_CON_CUPO, crearRecurrente, crearMovimiento, hoy,
  } from '../lib/db.js';
  import { formatear, interpretar, calcularGmf } from '../lib/dinero.js';
  import { guardar, borrar } from '../lib/repositorio.js';
  import { gmfDe, movimientoGmf } from '../lib/gmf.js';
  import {
    COBRO, CONOCIDAS, etiquetaFrecuencia, estadoDe, costoMensual, proximoCobro,
    resumenFijos, porPagar, proximosCobros, cobrosDebidos, movimientoDelCobro,
    idDelCobro, idDelGmfDelCobro, fechasDeCobro,
  } from '../lib/recurrentes.js';

  let {
    abierta = $bindable(false),
    recurrentes = [],
    cuentas = [],
    categorias = [],
    entidades = [],
    alCambiar = () => {},
    alAbrirMovimiento = () => {},
  } = $props();

  /**
   * La hora del movimiento. Si se escribió, esa. Si no, y es de hoy, la de
   * ahora. Si es de otro día y no se sabe, sin hora: inventarla sería dato falso.
   */
  function horaFinal(fecha, hora) {
    if (hora) return hora;
    return fecha === hoy() ? new Date().toTimeString().slice(0, 5) : null;
  }

  let vista = $state('lista');   // lista | nueva | editar | detalle | pagar | cancelar | pausar
  let elegida = $state(null);
  let guardando = $state(false);
  let error = $state('');
  let verCanceladas = $state(false);
  let version = $state(0);

  // ── Formulario ──
  let nombre = $state('');
  let icono = $state(null);
  let montoTexto = $state('');
  let montoVariable = $state(false);
  let frecuencia = $state('mensual');
  let fechaInicio = $state('');
  let anotarPasado = $state(false);
  let cobro = $state(COBRO.TARJETA);
  let cuentaId = $state(null);
  let categoriaId = $state(null);
  let pruebaGratis = $state(false);
  let avisarDias = $state(3);
  let notas = $state('');

  // ── Pagar a mano ──
  let ciclo = $state(null);            // la fecha de cobro que se está pagando
  let fechaPago = $state(hoy());
  let horaPago = $state('');
  let montoPagoTexto = $state('');
  let cuentaPago = $state(null);
  let metodoPago = $state(METODO_PAGO.EFECTIVO);
  let gmfManual = $state(null);

  // ── Pausar / cancelar ──
  let fechaAccion = $state(hoy());

  const FRECUENCIAS = [
    ['mensual', 'Mensual'], ['anual', 'Anual'], ['trimestral', 'Trimestral'],
    ['semestral', 'Semestral'], ['quincenal', 'Quincenal'], ['semanal', 'Semanal'],
  ];

  const FORMAS = [
    [COBRO.TARJETA, 'Se cobra sola a la tarjeta', 'Netflix en Nu. La app la anota sola cada vez.'],
    [COBRO.DEBITO, 'Débito automático de una cuenta', 'Sale sola del banco, con su 4x1000. También se anota sola.'],
    [COBRO.MANUAL, 'La pago yo', 'En efectivo, transferencia o PSE. La app te recuerda y tú confirmas.'],
  ];

  const METODOS_MANUAL = [
    [METODO_PAGO.TRANSFERENCIA, 'Transferencia'], [METODO_PAGO.PSE, 'PSE'],
    [METODO_PAGO.QR, 'QR'], [METODO_PAGO.OTRO, 'Otro'],
  ];

  const cuentaPorId = $derived(Object.fromEntries(cuentas.map((c) => [c.id, c])));
  const entidadDe = (c) => entidades.find((e) => e.id === c?.entidad_id) ?? null;

  const REALES = ['efectivo', 'ahorros', 'corriente', 'billetera'];
  const BANCARIAS = ['ahorros', 'corriente', 'billetera'];

  /** Las cuentas que tienen sentido según cómo se cobra. */
  const cuentasDelForm = $derived(
    cuentas.filter((c) => !c.archivada && (
      cobro === COBRO.TARJETA ? CUENTAS_CON_CUPO.includes(c.tipo)
      : cobro === COBRO.DEBITO ? BANCARIAS.includes(c.tipo)
      : REALES.includes(c.tipo)
    ))
  );
  $effect(() => {
    if (!cuentasDelForm.some((c) => c.id === cuentaId)) cuentaId = cuentasDelForm[0]?.id ?? null;
  });

  const cuentasPago = $derived(cuentas.filter((c) => !c.archivada && REALES.includes(c.tipo)));

  const subcategorias = $derived(
    categorias.filter((c) => c.tipo === 'egreso' && c.padre_id && !c.archivada)
      .sort((a, b) => a.nombre.localeCompare(b.nombre))
  );

  const monto = $derived(interpretar(montoTexto) ?? 0);
  const montoPago = $derived(interpretar(montoPagoTexto) ?? 0);

  // ─── Lo que se muestra ────────────────────────────────────────────────────

  const vivas = $derived(recurrentes.filter((r) => !r.borrado_en));
  const resumen = $derived(resumenFijos(vivas));

  function enDiasISO(n) {
    const d = new Date(Date.now() + n * 86400000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  /**
   * Cuáles cobros ya están anotados. Se pregunta a la base por los ids
   * fijos de cada cobro: vivos o borrados, cuentan como resueltos. Sirve
   * para tres cosas: no pedir que se pague lo ya pagado, no mostrar como
   * "próximo" lo que ya se cobró hoy, y saber cuál es el próximo de verdad.
   */
  let resueltos = $state(new Set());
  $effect(() => {
    const _v = version;
    const ids = new Set();
    for (const r of vivas) {
      const aviso = r.cobro === COBRO.MANUAL ? (r.avisar_dias_antes ?? 3) : 0;
      for (const f of cobrosDebidos(r, enDiasISO(Math.max(aviso, 0)))) ids.add(idDelCobro(r, f));
      for (const f of fechasDeCobro(r, hoy(), enDiasISO(400))) ids.add(idDelCobro(r, f));
    }
    const lista = [...ids];
    if (!lista.length) { resueltos = new Set(); return; }
    db.movimientos.bulkGet(lista).then((filas) => {
      resueltos = new Set(filas.filter(Boolean).map((m) => m.id));
    });
  });
  const pendientes = $derived(porPagar(vivas, resueltos));

  const proximos = $derived(
    proximosCobros(vivas.filter((r) => ['activa', 'prueba'].includes(estadoDe(r))), hoy(), 30)
      .filter((c) => !resueltos.has(idDelCobro(c.rec, c.fecha)))
  );

  /** El próximo cobro que todavía no ha pasado ni se anotó. */
  function proximoPendiente(r) {
    return fechasDeCobro(r, hoy(), enDiasISO(800))
      .find((f) => !resueltos.has(idDelCobro(r, f)) && !(r.saltados ?? []).includes(f)) ?? null;
  }

  // Los cobros de la suscripción abierta, para su historial.
  let cobrosDeElegida = $state([]);
  $effect(() => {
    const id = elegida?.id;
    const _v = version;
    if (!id) { cobrosDeElegida = []; return; }
    db.movimientos.where('recurrente_id').equals(id).toArray().then((l) => {
      if (elegida?.id === id) {
        cobrosDeElegida = l.filter((m) => !m.borrado_en && m.concepto !== '4x1000')
          .sort((a, b) => b.fecha.localeCompare(a.fecha));
      }
    });
  });

  $effect(() => {
    if (!elegida) return;
    const fresca = recurrentes.find((r) => r.id === elegida.id);
    if (fresca && fresca !== elegida) elegida = fresca;
  });

  // ── El 4x1000 de un pago a mano ──
  const cuentaDelPago = $derived(cuentaPorId[cuentaPago]);
  const pagoEnEfectivo = $derived(cuentaDelPago?.tipo === 'efectivo');
  const metodoReal = $derived(pagoEnEfectivo ? METODO_PAGO.EFECTIVO : metodoPago);
  const gmfAuto = $derived(gmfDe({
    tipo: 'egreso', monto: montoPago, cuentaOrigen: cuentaDelPago,
    entidad: entidadDe(cuentaDelPago), metodoPago: metodoReal,
  }));
  const gmf = $derived(gmfManual ?? gmfAuto.monto);

  // ─── Utilidades ───────────────────────────────────────────────────────────

  function fechaCorta(iso) {
    if (!iso) return '';
    return new Date(iso + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
  }
  function fechaLarga(iso) {
    if (!iso) return '';
    return new Date(iso + 'T12:00:00')
      .toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'long' });
  }
  function enDias(n) {
    return n === 0 ? 'hoy' : n === 1 ? 'mañana' : n < 0 ? `hace ${-n} día${n === -1 ? '' : 's'}` : `en ${n} días`;
  }

  function comoSeCobra(r) {
    const cuenta = cuentaPorId[r.cuenta_id]?.nombre;
    if (r.cobro === COBRO.TARJETA) return cuenta ? `Se cobra sola a ${cuenta}` : 'Se cobra sola a la tarjeta';
    if (r.cobro === COBRO.DEBITO) return cuenta ? `Débito automático de ${cuenta}` : 'Débito automático';
    return 'La pagas tú';
  }

  function refrescar() {
    version++;
    alCambiar();
  }

  // ─── Formulario ───────────────────────────────────────────────────────────

  function nueva() {
    nombre = '';
    icono = null;
    montoTexto = '';
    montoVariable = false;
    frecuencia = 'mensual';
    fechaInicio = '';
    anotarPasado = false;
    cobro = COBRO.TARJETA;
    categoriaId = null;
    pruebaGratis = false;
    avisarDias = 3;
    notas = '';
    error = '';
    vista = 'nueva';
  }

  function editar() {
    const r = elegida;
    nombre = r.nombre;
    icono = r.icono;
    montoTexto = String(r.monto);
    montoVariable = Boolean(r.monto_variable);
    frecuencia = r.frecuencia;
    fechaInicio = proximoPendiente(r) ?? r.fecha_inicio;
    anotarPasado = false;
    cobro = r.cobro;
    cuentaId = r.cuenta_id;
    categoriaId = r.categoria_id;
    pruebaGratis = Boolean(r.prueba_gratis) && estadoDe(r) === 'prueba';
    avisarDias = r.avisar_dias_antes ?? 3;
    notas = r.notas ?? '';
    error = '';
    vista = 'editar';
  }

  /** Tocar "Netflix" llena el nombre, la categoría y el ícono. El monto no. */
  function usarConocida(k) {
    nombre = k.nombre;
    icono = k.icono;
    montoVariable = Boolean(k.variable);
    const cat = categorias.find((c) => c.nombre === k.categoria && c.padre_id);
    if (cat) categoriaId = cat.id;
    if (k.nombre === 'Gimnasio') cobro = COBRO.MANUAL;
  }

  async function guardarForm() {
    error = '';
    if (!nombre.trim()) { error = 'Falta el nombre.'; return; }
    if (monto <= 0) { error = 'Falta cuánto cobra.'; return; }
    if (!fechaInicio) { error = 'Falta el día del próximo cobro.'; return; }
    if (!cuentaId && cobro !== COBRO.MANUAL) { error = 'Falta de qué tarjeta o cuenta sale.'; return; }

    guardando = true;
    try {
      const campos = {
        nombre: nombre.trim(),
        icono,
        monto,
        monto_variable: montoVariable,
        frecuencia,
        cobro,
        cuenta_id: cuentaId,
        metodo_pago: cobro === COBRO.MANUAL
          ? (cuentaPorId[cuentaId]?.tipo === 'efectivo' ? METODO_PAGO.EFECTIVO : METODO_PAGO.TRANSFERENCIA)
          : cobro === COBRO.TARJETA ? METODO_PAGO.CREDITO : METODO_PAGO.DEBITO_AUTOMATICO,
        categoria_id: categoriaId,
        prueba_gratis: pruebaGratis,
        avisar_dias_antes: Number(avisarDias) || 0,
        notas: notas.trim(),
      };

      if (vista === 'nueva') {
        // Desde cuándo se anotan cobros. Si el primer cobro ya pasó, solo se
        // anota si la persona lo pide: puede que ya lo haya anotado a mano.
        const registrada = fechaInicio < hoy() && anotarPasado ? fechaInicio : hoy();
        const rec = crearRecurrente({ ...campos, fecha_inicio: fechaInicio, registrada_en: registrada });
        await guardar('recurrentes', rec);
        elegida = rec;
      } else {
        // Cambiar el día de cobro mueve el calendario desde el próximo cobro;
        // lo ya cobrado se queda como está.
        const cambioDia = fechaInicio !== (proximoPendiente(elegida) ?? elegida.fecha_inicio);
        await guardar('recurrentes', {
          ...elegida,
          ...campos,
          ...(cambioDia ? { fecha_inicio: fechaInicio, registrada_en: hoy() > fechaInicio ? fechaInicio : hoy() } : {}),
        });
      }
      vista = 'detalle';
      refrescar();
    } catch (e) {
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  // ─── Pagar a mano ─────────────────────────────────────────────────────────

  function abrirPago(r, fechaCiclo) {
    elegida = r;
    ciclo = fechaCiclo;
    fechaPago = hoy();
    horaPago = '';
    montoPagoTexto = String(r.monto);
    cuentaPago = r.cuenta_id && cuentaPorId[r.cuenta_id] ? r.cuenta_id : cuentasPago[0]?.id;
    metodoPago = r.metodo_pago && r.metodo_pago !== METODO_PAGO.EFECTIVO ? r.metodo_pago : METODO_PAGO.TRANSFERENCIA;
    gmfManual = null;
    error = '';
    vista = 'pagar';
  }

  async function registrarPago() {
    if (guardando || !elegida || !ciclo || montoPago <= 0) return;
    guardando = true;
    error = '';
    try {
      const mov = crearMovimiento(movimientoDelCobro(elegida, ciclo, {
        fecha: fechaPago,
        hora: horaFinal(fechaPago, horaPago),
        monto: montoPago,
        cuenta_origen: cuentaPago,
        metodo_pago: metodoReal,
        revisado: true,
        gmf,
        gmf_revisado: true,
      }));
      await guardar('movimientos', mov);
      if (gmf > 0) {
        await guardar('movimientos', {
          ...crearMovimiento(movimientoGmf(mov, gmf)),
          id: idDelGmfDelCobro(elegida, ciclo),
          recurrente_id: elegida.id,
        });
      }
      vista = 'detalle';
      refrescar();
    } catch (e) {
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  /** "Este mes no": el ciclo queda resuelto sin cobro. */
  async function saltarCiclo(r, fechaCiclo) {
    guardando = true;
    try {
      await guardar('recurrentes', {
        ...r,
        saltados: [...new Set([...(r.saltados ?? []), fechaCiclo])],
      });
      if (vista === 'pagar') vista = 'detalle';
      refrescar();
    } catch (e) {
      error = `No se pudo guardar: ${e?.message ?? e}`;
    } finally {
      guardando = false;
    }
  }

  // ─── Pausar, reanudar, cancelar, borrar ───────────────────────────────────

  async function actualizar(campos, siguiente = 'detalle') {
    guardando = true;
    error = '';
    try {
      await guardar('recurrentes', { ...elegida, ...campos });
      vista = siguiente;
      refrescar();
    } catch (e) {
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  function pausar() {
    actualizar({ pausada_desde: hoy(), pausada_hasta: fechaAccion || null });
  }
  function reanudar() {
    actualizar({ pausada_desde: null, pausada_hasta: null, registrada_en: hoy() });
  }
  function cancelar() {
    actualizar({ cancelada_en: fechaAccion || hoy() });
  }
  function reactivar() {
    // Lo que no se cobró mientras estuvo cancelada no se inventa.
    actualizar({ cancelada_en: null, registrada_en: hoy() });
  }

  async function borrarSuscripcion() {
    if (!confirm(`¿Borrar "${elegida.nombre}"? Los cobros que ya se anotaron se quedan: esa plata sí salió.`)) return;
    guardando = true;
    try {
      await borrar('recurrentes', elegida.id);
      elegida = null;
      vista = 'lista';
      refrescar();
    } catch (e) {
      error = `No se pudo borrar: ${e?.message ?? e}`;
    } finally {
      guardando = false;
    }
  }

  function abrirDetalle(r) {
    elegida = r;
    error = '';
    vista = 'detalle';
  }

  const titulo = $derived(
    vista === 'nueva' ? 'Nueva suscripción o pago fijo'
    : vista === 'editar' ? 'Corregir'
    : vista === 'pagar' ? `Pagar ${elegida?.nombre ?? ''}`
    : vista === 'cancelar' ? 'Cancelar'
    : vista === 'pausar' ? 'Pausar'
    : vista === 'detalle' ? elegida?.nombre ?? ''
    : 'Suscripciones y fijos'
  );

  function atras() {
    error = '';
    if (['editar', 'pagar', 'cancelar', 'pausar'].includes(vista)) vista = 'detalle';
    else if (vista === 'detalle' || vista === 'nueva') { vista = 'lista'; elegida = null; }
    else abierta = false;
  }
</script>

{#if abierta}
  <div class="pantalla">
    <header>
      <button class="volver" onclick={atras} aria-label="Volver">←</button>
      <h1>{titulo}</h1>
    </header>

    <div class="contenido">
      {#if error}<p class="error" role="alert">{error}</p>{/if}

      <!-- ══ LISTA ══════════════════════════════════════════════════════ -->
      {#if vista === 'lista'}
        <div class="destacado">
          <p class="rotulo">En fijos se te van</p>
          <p class="cifra grande">{formatear(resumen.mensual)} <span class="unidad">al mes</span></p>
          {#if resumen.mensual > 0}
            <p class="frase">Son <strong class="cifra">{formatear(resumen.anual)}</strong> al año.</p>
          {/if}
        </div>

        <button class="principal ancho" onclick={nueva}>Agregar suscripción o pago fijo</button>

        {#if resumen.pruebasPorVencer.length}
          {#each resumen.pruebasPorVencer as p (p.rec.id)}
            <div class="aviso ojo">
              <p><strong>La prueba gratis de {p.rec.nombre} termina {enDias(p.dias)}.</strong></p>
              <p class="explica">
                Después te cobra {formatear(p.rec.monto)} {etiquetaFrecuencia(p.rec.frecuencia)}.
                Si no la vas a usar, cancélala antes.
              </p>
            </div>
          {/each}
        {/if}

        {#if pendientes.length}
          <section>
            <h2>Te toca pagar</h2>
            {#each pendientes as p (p.id)}
              <div class="pendiente" class:tarde={p.dias < 0}>
                <span class="icono" aria-hidden="true">{p.rec.icono ?? '🔁'}</span>
                <div class="datos">
                  <p class="nombre">{p.rec.nombre}</p>
                  <p class="meta" class:tarde={p.dias < 0}>
                    {formatear(p.rec.monto)} · {p.dias < 0 ? 'se pasó ' : 'vence '}{enDias(p.dias)}
                  </p>
                </div>
                <div class="acciones-fila">
                  <button class="chip activo" onclick={() => abrirPago(p.rec, p.fecha)}>Ya lo pagué</button>
                  <button class="chip" onclick={() => saltarCiclo(p.rec, p.fecha)}>Este mes no</button>
                </div>
              </div>
            {/each}
          </section>
        {/if}

        {#if proximos.length}
          <section>
            <h2>Próximos cobros · 30 días</h2>
            {#each proximos.slice(0, 8) as c (c.rec.id + c.fecha)}
              <button class="fila" onclick={() => abrirDetalle(c.rec)}>
                <span class="dia cifra">{fechaCorta(c.fecha)}</span>
                <div class="datos">
                  <p class="nombre">{c.rec.icono ?? ''} {c.rec.nombre}</p>
                  <p class="meta">{comoSeCobra(c.rec)} · {enDias(c.dias)}</p>
                </div>
                <span class="cifra monto">{formatear(c.monto)}</span>
              </button>
            {/each}
          </section>
        {/if}

        {#if resumen.cobrando.length || resumen.pausada.length}
          <section>
            <h2>Tus suscripciones y fijos</h2>
            {#each [...resumen.cobrando, ...resumen.pausada] as r (r.id)}
              {@const estado = estadoDe(r)}
              <button class="fila" onclick={() => abrirDetalle(r)}>
                <span class="icono" aria-hidden="true">{r.icono ?? '🔁'}</span>
                <div class="datos">
                  <p class="nombre">
                    {r.nombre}
                    {#if estado === 'prueba'}<span class="etiqueta-estado prueba">prueba</span>{/if}
                    {#if estado === 'pausada'}<span class="etiqueta-estado">pausada</span>{/if}
                  </p>
                  <p class="meta">{comoSeCobra(r)}</p>
                </div>
                <div class="derecha">
                  <span class="cifra monto">{formatear(r.monto)}</span>
                  <span class="meta">{etiquetaFrecuencia(r.frecuencia)}</span>
                </div>
              </button>
            {/each}
          </section>
        {:else}
          <div class="vacio">
            <p class="vacio-titulo">No hay suscripciones anotadas</p>
            <p class="explica">
              Netflix, Spotify, el gimnasio, el plan del celular, el internet:
              lo que se cobra solo o hay que pagar cada mes. Anótalas una vez y
              la app te dice cuánto se te va al año, te avisa antes de cada
              cobro y anota sola lo que se cobra solo.
            </p>
          </div>
        {/if}

        {#if resumen.cancelada.length}
          <section>
            <button class="plegable" onclick={() => (verCanceladas = !verCanceladas)}>
              <span>Canceladas · {resumen.cancelada.length}</span>
              <span class="flecha" class:abierta={verCanceladas} aria-hidden="true">›</span>
            </button>
            {#if verCanceladas}
              {#each resumen.cancelada as r (r.id)}
                <button class="fila cerrada" onclick={() => abrirDetalle(r)}>
                  <span class="icono" aria-hidden="true">{r.icono ?? '🔁'}</span>
                  <div class="datos">
                    <p class="nombre">{r.nombre}</p>
                    <p class="meta">Cancelada el {fechaCorta(r.cancelada_en)}</p>
                  </div>
                  <span class="cifra monto apagado">{formatear(r.monto)}</span>
                </button>
              {/each}
            {/if}
          </section>
        {/if}

      <!-- ══ NUEVA O CORREGIR ═══════════════════════════════════════════ -->
      {:else if vista === 'nueva' || vista === 'editar'}
        {#if vista === 'nueva'}
          <div class="chips">
            {#each CONOCIDAS as k (k.nombre)}
              <button class="chip" class:activo={nombre === k.nombre} onclick={() => usarConocida(k)}>
                {k.icono} {k.nombre}
              </button>
            {/each}
          </div>
        {/if}

        <label>
          <span class="etiqueta">Nombre</span>
          <input type="text" bind:value={nombre} placeholder="Netflix, gimnasio, plan del celular…" />
        </label>

        <label>
          <span class="etiqueta">Cuánto cobra</span>
          <input class="monto-grande cifra" type="text" inputmode="numeric" bind:value={montoTexto} placeholder="0" />
        </label>
        <label class="casilla">
          <input type="checkbox" bind:checked={montoVariable} />
          <span>Cambia cada vez (cobra en dólares, o según consumo)</span>
        </label>

        <div class="campo">
          <span class="etiqueta">Cada cuánto</span>
          <div class="chips">
            {#each FRECUENCIAS as [valor, texto]}
              <button class="chip" class:activo={frecuencia === valor} onclick={() => (frecuencia = valor)}>{texto}</button>
            {/each}
          </div>
        </div>

        <label>
          <span class="etiqueta">{pruebaGratis ? 'Primer cobro (cuando termina la prueba)' : 'Día del próximo cobro'}</span>
          <input type="date" bind:value={fechaInicio} />
        </label>
        {#if vista === 'nueva' && fechaInicio && fechaInicio < hoy()}
          <label class="casilla">
            <input type="checkbox" bind:checked={anotarPasado} />
            <span>Ese cobro ya pasó: anotarlo también. Déjalo sin marcar si ya lo anotaste a mano.</span>
          </label>
        {/if}
        <label class="casilla">
          <input type="checkbox" bind:checked={pruebaGratis} />
          <span>Estoy en prueba gratis: hasta ese día no cobra</span>
        </label>

        <div class="campo">
          <span class="etiqueta">¿Cómo se paga?</span>
          <div class="opciones">
            {#each FORMAS as [valor, texto, nota]}
              <button class="opcion" class:activa={cobro === valor} onclick={() => (cobro = valor)}>
                <span class="opcion-titulo">{texto}</span>
                <span class="opcion-nota">{nota}</span>
              </button>
            {/each}
          </div>
        </div>

        <label>
          <span class="etiqueta">
            {cobro === COBRO.TARJETA ? 'A qué tarjeta' : cobro === COBRO.DEBITO ? 'De qué cuenta' : 'Con qué la pagas normalmente'}
          </span>
          {#if cuentasDelForm.length}
            <select bind:value={cuentaId}>
              {#each cuentasDelForm as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
            </select>
          {:else}
            <span class="ayuda">
              {cobro === COBRO.TARJETA
                ? 'No tienes ninguna tarjeta ni crédito anotado. Agrégala en Cuentas.'
                : 'No tienes cuentas de banco anotadas. Agrégala en Cuentas.'}
            </span>
          {/if}
        </label>

        <label>
          <span class="etiqueta">Categoría</span>
          <select bind:value={categoriaId}>
            <option value={null}>Sin categoría</option>
            {#each subcategorias as c}<option value={c.id}>{c.nombre}</option>{/each}
          </select>
        </label>

        <label>
          <span class="etiqueta">Avisarme cuántos días antes</span>
          <input class="cifra corto" type="number" min="0" max="15" bind:value={avisarDias} />
        </label>

        <label>
          <span class="etiqueta">Notas</span>
          <input type="text" bind:value={notas} placeholder="Plan familiar, lo comparto con…" />
        </label>

        {#if monto > 0}
          <p class="explica">
            Eso son <strong class="cifra">{formatear(costoMensual({ monto, frecuencia }))}</strong> al mes
            y <strong class="cifra">{formatear(costoMensual({ monto, frecuencia }) * 12)}</strong> al año.
          </p>
        {/if}

        <div class="botones">
          <button class="secundario" onclick={atras}>Cancelar</button>
          <button class="principal" disabled={guardando} onclick={guardarForm}>
            {guardando ? 'Guardando…' : vista === 'nueva' ? 'Guardar' : 'Guardar cambios'}
          </button>
        </div>

      <!-- ══ DETALLE ════════════════════════════════════════════════════ -->
      {:else if vista === 'detalle' && elegida}
        {@const estado = estadoDe(elegida)}
        {@const proximo = proximoPendiente(elegida)}
        {@const pendientesDeEsta = pendientes.filter((p) => p.rec.id === elegida.id)}
        {@const pagado = cobrosDeElegida.reduce((t, m) => t + m.monto, 0)}

        <div class="destacado">
          <p class="rotulo">{elegida.icono ?? '🔁'} {comoSeCobra(elegida)}</p>
          <p class="cifra grande">
            {formatear(elegida.monto)} <span class="unidad">{etiquetaFrecuencia(elegida.frecuencia)}</span>
          </p>
          <p class="frase">
            {#if estado === 'cancelada'}
              Cancelada desde el {fechaLarga(elegida.cancelada_en)}.
            {:else if estado === 'pausada'}
              En pausa{elegida.pausada_hasta ? ` hasta el ${fechaLarga(elegida.pausada_hasta)}` : ''}.
            {:else if estado === 'prueba'}
              En prueba gratis. Empieza a cobrar el {fechaLarga(elegida.fecha_inicio)}.
            {:else if proximo}
              Próximo cobro: {fechaLarga(proximo)}.
            {/if}
          </p>
          {#if estado !== 'cancelada'}
            <p class="ayuda">Te cuesta {formatear(costoMensual(elegida) * 12)} al año.</p>
          {/if}
          {#if elegida.monto_variable}
            <p class="ayuda">El monto cambia cada vez: revisa cada cobro contra el extracto.</p>
          {/if}
          {#if elegida.notas}<p class="notas">{elegida.notas}</p>{/if}
        </div>

        {#each pendientesDeEsta as p (p.id)}
          <div class="aviso" class:ojo={p.dias < 0}>
            <p><strong>{p.dias < 0 ? 'Se pasó' : 'Toca pagar'} la del {fechaLarga(p.fecha)}.</strong></p>
            <div class="botones">
              <button class="principal" onclick={() => abrirPago(elegida, p.fecha)}>Ya lo pagué</button>
              <button class="secundario" onclick={() => saltarCiclo(elegida, p.fecha)}>Este mes no</button>
            </div>
          </div>
        {/each}

        <div class="botones">
          <button class="secundario" onclick={editar}>Corregir</button>
          {#if estado === 'pausada'}
            <button class="secundario" onclick={reanudar}>Reanudar</button>
          {:else if estado !== 'cancelada'}
            <button class="secundario" onclick={() => { fechaAccion = ''; vista = 'pausar'; }}>Pausar</button>
          {/if}
          {#if estado === 'cancelada'}
            <button class="secundario" onclick={reactivar}>Reactivar</button>
          {:else}
            <button class="secundario" onclick={() => { fechaAccion = hoy(); vista = 'cancelar'; }}>Cancelar</button>
          {/if}
        </div>

        <section>
          <h2>Cobros anotados{#if pagado > 0} · <span class="cifra">{formatear(pagado)}</span>{/if}</h2>
          {#if cobrosDeElegida.length}
            {#each cobrosDeElegida as m (m.id)}
              <button class="fila" onclick={() => alAbrirMovimiento(m)}>
                <span class="dia cifra">{fechaCorta(m.fecha)}</span>
                <div class="datos">
                  <p class="nombre">{m.concepto}</p>
                  <p class="meta" class:tarde={m.revisado === false}>
                    {m.revisado === false ? 'Revisa el monto con el extracto' : (cuentaPorId[m.cuenta_origen]?.nombre ?? '')}
                  </p>
                </div>
                <span class="cifra monto">{formatear(m.monto)}</span>
              </button>
            {/each}
          {:else}
            <p class="ayuda">
              Todavía no hay cobros.
              {#if elegida.cobro !== COBRO.MANUAL}Se anotan solos el día que te cobran.{/if}
            </p>
          {/if}
        </section>

        <button class="peligro" onclick={borrarSuscripcion} disabled={guardando}>Borrar esta suscripción</button>
        <p class="ayuda">
          Borrarla no borra los cobros que ya se anotaron: esa plata sí salió.
          Si ya no la pagas, mejor "Cancelar": queda en el historial.
        </p>

      <!-- ══ PAGAR A MANO ═══════════════════════════════════════════════ -->
      {:else if vista === 'pagar' && elegida}
        <div class="destacado">
          <p class="rotulo">La del {fechaLarga(ciclo)}</p>
          <p class="cifra grande">{formatear(montoPago)}</p>
        </div>

        <label>
          <span class="etiqueta">Cuánto pagaste</span>
          <input class="monto-grande cifra" type="text" inputmode="numeric" bind:value={montoPagoTexto} />
        </label>

        <div class="par">
          <label>
            <span class="etiqueta">Cuándo</span>
            <div class="fecha-hora">
              <input type="date" bind:value={fechaPago} max={hoy()} />
              <input type="time" bind:value={horaPago} aria-label="Hora (opcional)" />
            </div>
          </label>
          <label>
            <span class="etiqueta">De qué cuenta</span>
            <select bind:value={cuentaPago} onchange={() => { if (gmfManual !== 0) gmfManual = null; }}>
              {#each cuentasPago as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
            </select>
          </label>
        </div>

        {#if !pagoEnEfectivo}
          <div class="chips" role="radiogroup" aria-label="Cómo pagaste">
            {#each METODOS_MANUAL as [valor, texto]}
              <button class="chip" class:activo={metodoPago === valor} role="radio" aria-checked={metodoPago === valor}
                      onclick={() => { metodoPago = valor; if (gmfManual !== 0) gmfManual = null; }}>{texto}</button>
            {/each}
          </div>
        {/if}

        {#if montoPago > 0 && !pagoEnEfectivo}
          <div class="gmf">
            {#if gmf > 0}
              <span>4x1000: <strong class="cifra">{formatear(gmf)}</strong></span>
              <button class="enlace" onclick={() => (gmfManual = 0)}>No me lo cobraron</button>
            {:else}
              <span class="sin-gmf">
                Sin 4x1000
                {#if gmfManual === 0}(dijiste que no te lo cobraron){:else if gmfAuto.razon}({gmfAuto.razon}){/if}
              </span>
              {#if gmfManual === 0 && gmfAuto.monto > 0}
                <button class="enlace" onclick={() => (gmfManual = null)}>Sí me lo cobraron</button>
              {:else if gmfAuto.monto === 0}
                <button class="enlace" onclick={() => (gmfManual = calcularGmf(montoPago))}>Sí me lo cobraron</button>
              {/if}
            {/if}
          </div>
        {/if}

        <div class="botones">
          <button class="secundario" onclick={() => saltarCiclo(elegida, ciclo)}>Este mes no</button>
          <button class="principal" disabled={guardando || montoPago <= 0} onclick={registrarPago}>
            {guardando ? 'Guardando…' : 'Anotar el pago'}
          </button>
        </div>

      <!-- ══ PAUSAR ═════════════════════════════════════════════════════ -->
      {:else if vista === 'pausar' && elegida}
        <p class="explica">
          Mientras esté en pausa no se anotan cobros ni llegan avisos. Sirve
          para el gimnasio en vacaciones, o una plataforma que dejas de pagar un
          tiempo.
        </p>
        <label>
          <span class="etiqueta">¿Hasta cuándo?</span>
          <input type="date" bind:value={fechaAccion} min={hoy()} />
          <span class="ayuda">Déjalo vacío si no sabes: la reanudas a mano.</span>
        </label>
        <div class="botones">
          <button class="secundario" onclick={atras}>Volver</button>
          <button class="principal" disabled={guardando} onclick={pausar}>Pausar</button>
        </div>

      <!-- ══ CANCELAR ═══════════════════════════════════════════════════ -->
      {:else if vista === 'cancelar' && elegida}
        <p class="explica">
          Desde ese día no se anotan más cobros. Lo ya cobrado se queda. Te
          ahorras <strong class="cifra">{formatear(costoMensual(elegida) * 12)}</strong> al año.
        </p>
        <label>
          <span class="etiqueta">¿Desde cuándo ya no cobra?</span>
          <input type="date" bind:value={fechaAccion} />
          <span class="ayuda">
            Si cancelaste pero ya te cobraron el mes, pon el día siguiente al
            último cobro.
          </span>
        </label>
        <div class="botones">
          <button class="secundario" onclick={atras}>Volver</button>
          <button class="principal" disabled={guardando || !fechaAccion} onclick={cancelar}>Cancelar la suscripción</button>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .pantalla {
    position: fixed;
    inset: 0;
    z-index: var(--z-hoja);
    background: var(--bg-canvas);
    overflow-y: auto;
    max-width: var(--w-app);
    margin-inline: auto;
    animation: entrar var(--d-normal) var(--ease-entrada);
  }
  @keyframes entrar {
    from { opacity: 0; transform: translateX(16px) }
    to   { opacity: 1; transform: translateX(0) }
  }

  header {
    position: sticky;
    top: 0;
    z-index: var(--z-pegajoso);
    display: flex;
    align-items: center;
    gap: var(--e-2);
    padding: calc(var(--e-3) + var(--safe-t)) var(--gutter) var(--e-3);
    background: var(--bg-canvas);
    border-bottom: 1px solid var(--border);
  }
  .volver {
    width: var(--tap);
    height: var(--tap);
    display: grid;
    place-items: center;
    font-size: var(--t-xl);
    border-radius: var(--r-full);
    margin-left: calc(var(--e-3) * -1);
  }
  h1 { font-size: var(--t-lg); font-weight: var(--w-semibold) }

  .contenido {
    padding: var(--e-4) var(--gutter) calc(var(--e-16) + var(--safe-b));
    display: flex;
    flex-direction: column;
    gap: var(--e-4);
  }

  section { display: flex; flex-direction: column; gap: var(--e-2) }
  h2 {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }

  .explica { font-size: var(--t-sm); color: var(--ink-2); line-height: var(--lh-loose) }
  .ayuda { font-size: var(--t-xs); color: var(--ink-3); line-height: var(--lh-loose) }
  .notas { font-size: var(--t-xs); color: var(--ink-3); margin-top: var(--e-2) }

  .error {
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--alerta-suave);
    color: var(--alerta);
    font-size: var(--t-sm);
    line-height: var(--lh-loose);
  }

  .vacio { padding: var(--e-6) 0; text-align: center }
  .vacio-titulo {
    font-size: var(--t-lg);
    font-weight: var(--w-semibold);
    color: var(--ink-2);
    margin-bottom: var(--e-2);
  }

  /* ── La caja de arriba ── */
  .destacado {
    padding: var(--e-4);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
  }
  .rotulo { font-size: var(--t-sm); color: var(--ink-3) }
  .grande {
    font-size: var(--t-3xl);
    font-weight: var(--w-bold);
    letter-spacing: var(--track-tight);
    line-height: var(--lh-tight);
  }
  .unidad { font-size: var(--t-base); font-weight: var(--w-medium); color: var(--ink-3); letter-spacing: 0 }
  .frase { font-size: var(--t-sm); color: var(--ink-2); margin-top: var(--e-1) }

  /* ── Filas ── */
  .fila, .pendiente {
    display: grid;
    grid-template-columns: 44px 1fr auto;
    gap: var(--e-3);
    align-items: center;
    padding: var(--e-3);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    text-align: left;
    width: 100%;
  }
  .fila { transition: transform var(--d-instant) var(--ease-salida) }
  .fila:active { transform: scale(0.99) }
  .fila.cerrada { opacity: 0.62 }
  .pendiente { grid-template-columns: 40px 1fr; row-gap: var(--e-2) }
  .pendiente.tarde { border-color: var(--alerta) }
  .acciones-fila { grid-column: 1 / -1; display: flex; gap: var(--e-2) }

  .icono {
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    font-size: var(--t-lg);
  }
  .dia {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    color: var(--ink-3);
    text-align: center;
    text-transform: uppercase;
  }
  .datos { min-width: 0 }
  .derecha { text-align: right; display: flex; flex-direction: column }
  .nombre { font-weight: var(--w-medium) }
  .meta { font-size: var(--t-xs); color: var(--ink-3) }
  .tarde { color: var(--alerta) }
  .monto { font-weight: var(--w-semibold); white-space: nowrap }
  .monto.apagado { color: var(--ink-3); font-weight: var(--w-medium) }

  .etiqueta-estado {
    display: inline-block;
    margin-left: var(--e-1);
    padding: 0 var(--e-2);
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    font-size: var(--t-xs);
    font-weight: var(--w-medium);
    color: var(--ink-3);
    vertical-align: middle;
  }
  .etiqueta-estado.prueba { background: var(--alerta-suave); color: var(--alerta) }

  .plegable {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: var(--e-2) 0;
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }
  .flecha { transition: transform var(--d-rapido) var(--ease-salida); font-size: var(--t-lg) }
  .flecha.abierta { transform: rotate(90deg) }

  /* ── Formularios ── */
  label, .campo { display: flex; flex-direction: column; gap: var(--e-1) }
  .etiqueta { font-size: var(--t-sm); font-weight: var(--w-medium) }
  .par { display: grid; grid-template-columns: 1fr 1fr; gap: var(--e-3) }

  input:not([type='checkbox']), select {
    padding: var(--e-3);
    font-size: var(--t-base);
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
    width: 100%;
  }
  .monto-grande { font-size: var(--t-2xl); font-weight: var(--w-bold); text-align: center }
  .corto { max-width: 96px }

  .casilla {
    flex-direction: row;
    align-items: flex-start;
    gap: var(--e-2);
    font-size: var(--t-sm);
    color: var(--ink-2);
    line-height: var(--lh-loose);
  }
  .casilla input { width: 20px; height: 20px; margin-top: 2px; accent-color: var(--acento); flex-shrink: 0 }

  .chips { display: flex; gap: var(--e-2); flex-wrap: wrap }
  .chip {
    min-height: 38px;
    padding: 0 var(--e-3);
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    font-size: var(--t-sm);
    font-weight: var(--w-medium);
  }
  .chip.activo { background: var(--acento); color: var(--on-acento) }

  .opciones { display: flex; flex-direction: column; gap: var(--e-2) }
  .opcion {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left;
    padding: var(--e-3);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    background: var(--bg-surface);
    transition: border-color var(--d-instant) var(--ease-salida);
  }
  .opcion.activa { border-color: var(--acento); box-shadow: inset 0 0 0 1px var(--acento) }
  .opcion-titulo { font-size: var(--t-sm); font-weight: var(--w-semibold) }
  .opcion-nota { font-size: var(--t-xs); color: var(--ink-3); line-height: var(--lh-loose) }

  .aviso {
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    border-left: 3px solid var(--acento);
    display: flex;
    flex-direction: column;
    gap: var(--e-2);
    font-size: var(--t-sm);
  }
  .aviso.ojo { border-left-color: var(--alerta) }
  .aviso .secundario { background: var(--bg-surface); border: 1px solid var(--border-strong) }

  .gmf {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--e-2);
    flex-wrap: wrap;
    font-size: var(--t-sm);
    padding: var(--e-2) var(--e-3);
    background: var(--bg-sunken);
    border-radius: var(--r-md);
  }
  .sin-gmf { color: var(--ink-3) }
  .enlace {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    color: var(--acento);
    text-decoration: underline;
    text-underline-offset: 2px;
    min-height: 32px;
  }

  .botones { display: flex; gap: var(--e-2) }
  .principal {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--acento);
    color: var(--on-acento);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }
  .principal.ancho { width: 100% }
  .principal:disabled { opacity: 0.4 }
  .secundario {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .peligro {
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: transparent;
    color: var(--peligro);
    font-weight: var(--w-medium);
    min-height: var(--tap);
    border: 1px solid var(--border-strong);
  }
  /* Fecha y hora juntas: el orden dentro del día también cuenta. */
  .fecha-hora { display: flex; flex-direction: column; gap: var(--e-1) }
</style>
